import * as React from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import CatalogDate from '@/components/CatalogDate/CatalogDate';
import Countdown from '@liveauctioneers/caterwaul-components/lib/Countdown/Countdown';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getCatalog } from '@/redux/modules/catalog';
import { getItem } from '@/redux/modules/item';
import { getLiveCatalogData } from '@/redux/modules/console';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

type Props = {
    catalogId: number;
    itemId: number;
    onCountdownEnd?: Function;
    showCountdown?: boolean;
};

const ItemBiddingEstimate = (props: Props) => {
    const { catalogId, itemId, onCountdownEnd, showCountdown = true } = props;

    const { highBidEstimate, index, lowBidEstimate } = useSelector((state) => getItem(state, itemId), isEqual);
    const { catalogStatus, currency, saleStartTs } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { isPassed, isSold } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const { currentItemIndex } = useSelector((state) => getLiveCatalogData(state, catalogId), isEqual);

    const isCatalogLive = catalogStatus === 'live';
    const itemIndexOffset = index - currentItemIndex;

    const { formatMessage } = useIntl();

    const estimateRange = {
        high: <FormattedCurrency noFraction sourceCurrency={currency} value={highBidEstimate} />,
        low: <FormattedCurrency noFraction sourceCurrency={currency} value={lowBidEstimate} />,
    };
    // custom messaging depending on position of lot in a live catalog
    const lotPositionText =
        itemIndexOffset === 1 ? (
            <FormattedMessage id="itemBidding.lotAway" />
        ) : itemIndexOffset === -1 ? (
            <FormattedMessage id="itemBidding.lotAgo" />
        ) : itemIndexOffset === 0 ? (
            <FormattedMessage id="itemBidding.liveNow" />
        ) : itemIndexOffset > 1 ? (
            <FormattedMessage
                id="itemBidding.lotsAway"
                values={{ lots: <FormattedNumber value={itemIndexOffset} /> }}
            />
        ) : (
            <FormattedMessage
                id="itemBidding.lotsAgo"
                values={{ lots: <FormattedNumber value={Math.abs(itemIndexOffset)} /> }}
            />
        );
    // shows custom lot position message if live catalog
    // otherwise shows countdown to catalog start if upcoming
    // or past date if catalog is done

    const countdownText = isCatalogLive ? (
        <>
            <LiveSpan id="lotPosition">{lotPositionText}</LiveSpan>
        </>
    ) : isSold || isPassed ? (
        <div>
            <CatalogDate date={saleStartTs} showDate />
        </div>
    ) : (
        <StyledCountdown
            daysText={formatMessage({ id: 'day' })}
            extendedCount={false}
            hoursText={formatMessage({ id: 'hr' })}
            minutesText={formatMessage({ id: 'min' })}
            onCountdownEnd={onCountdownEnd}
            secondsText={formatMessage({ id: 'sec' })}
            seperatorText=""
            showSeconds
            timeStamp={saleStartTs}
        />
    );

    return (
        <EstimateRowDiv>
            <StyledEstimateAmounts>
                <FormattedMessage id="itemBidding.estimate" values={estimateRange} />
            </StyledEstimateAmounts>
            {showCountdown && countdownText}
        </EstimateRowDiv>
    );
};

export default React.memo<Props>(ItemBiddingEstimate, isEqual);

const LiveSpan = styled.span`
    color: ${({ theme }) => theme.colors.red300};
    font-size: 13px;

    & > span {
        margin-left: 4px;
    }
`;

const StyledCountdown = styled(Countdown)`
    @media screen {
        &&& {
            color: ${({ theme }) => theme.colors.red300};
            display: block;
            font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
            font-size: unset;
        }
    }
`;

const EstimateRowDiv = styled.div`
    display: flex;
    justify-content: space-between;

    @media print {
        text-align: center;

        > span,
        > div {
            margin: 7px auto;
        }
    }
`;

const StyledEstimateAmounts = styled.div`
    display: flex;
    span:first-child {
        margin-left: 5px;
    }
    > div {
        margin: 0 5px;
    }
`;
